import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { has, isArray } from "lodash";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import api, { openApi } from "../../services/api";
import { disconnectSocket, getSocket } from "../../services/socket";
import { db } from "../../services/database";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});

  openApi.interceptors.request.use(
    (config) => {
      if (config.includeToken !== false) {
        const token = localStorage.getItem("token");
        if (token) {
          let tokenClean = token.replace(/"/g, '');
          config.headers["Authorization"] = `Bearer ${tokenClean}`;
          setIsAuth(true);
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        let tokenClean = token.replace(/"/g, '');
        config.headers["Authorization"] = `Bearer ${tokenClean}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Manejar error 403 (token expirado)
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) {
            throw new Error("No refresh token available");
          }
          const { data } = await openApi.post("/auth/refresh_token", { refreshToken });
          if (data?.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
          }
          if (data?.refreshToken) {
            localStorage.setItem("refreshToken", data.refreshToken);
          }
          if (data?.user?.id) {
            localStorage.setItem("userId", data.user.id);
          }
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error("Error al refrescar token:", refreshError);
          // Solo cerrar sesión si el refresh también falla
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("companyId");
          localStorage.removeItem("userId");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          return Promise.reject(refreshError);
        }
      }
      
      // Manejar error 401 (no autorizado) - ser más selectivo
       if (error?.response?.status === 401) {
         // Solo cerrar sesión si es un error de autenticación real, no errores de socket o filtros
         const url = error?.config?.url || '';
         const errorMessage = error?.response?.data?.message || '';
         
         // Identificar errores de autenticación reales
         const isAuthError = url.includes('/auth/') || 
                            url.includes('/refresh_token') ||
                            url.includes('/login') ||
                            errorMessage.toLowerCase().includes('token') ||
                            errorMessage.toLowerCase().includes('unauthorized') ||
                            errorMessage.toLowerCase().includes('invalid token') ||
                            errorMessage.toLowerCase().includes('expired');
         
         // Excluir errores que NO deben cerrar sesión
         const isFilterError = url.includes('/tickets') && errorMessage.includes('filter');
         const isSocketError = url.includes('socket') || errorMessage.includes('socket');
         const isTicketError = url.includes('/tickets') && !errorMessage.toLowerCase().includes('token');
         
         if (isAuthError && !isFilterError && !isSocketError && !isTicketError) {
           console.log("Error de autenticación detectado, cerrando sesión:", {
             url: url,
             message: errorMessage,
             status: error?.response?.status
           });
           localStorage.removeItem("token");
           localStorage.removeItem("companyId");
           api.defaults.headers.Authorization = undefined;
           setIsAuth(false);
         } else {
           console.log("Error 401 no relacionado con autenticación, ignorando:", {
             url: url,
             message: errorMessage,
             isFilterError,
             isSocketError,
             isTicketError
           });
         }
       }
      
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    console.log("Token en localStorage:", token);
    (async () => {
      if (token && refreshToken) {
        try {
          console.log("Intentando refresh token...");
          const { data } = await openApi.post("/auth/refresh_token", { refreshToken });
          console.log("Respuesta del refresh:", data);

          let tokenClean = data.token.replace(/"/g, '');
          api.defaults.headers.Authorization = `Bearer ${tokenClean}`;
          localStorage.setItem("token", JSON.stringify(data.token));
          if (data.refreshToken) {
            localStorage.setItem("refreshToken", data.refreshToken);
          }
          if (data.user?.id) {
            localStorage.setItem("userId", data.user.id);
          }
          console.log("Nuevo token guardado:", data.token);
          console.log("Usuario recibido:", data.user);

          setIsAuth(true);
          setUser(data.user);
        } catch (err) {
          console.log("Error en refresh token:", err);
          console.log("Error response:", err.response?.data);
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("companyId");
          localStorage.removeItem("userId");
          localStorage.removeItem("cshow");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          setUser({});
          history.push("/login");
        }
      } else {
        console.log("No hay token en localStorage");
      }
      setLoading(false);
    })();
  }, [history]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = getSocket(companyId);

    if (!socket) return

    if (!user.languageId) {
      localStorage.removeItem("i18nextLng");
    } else {
      const language = user.languageId === 2 ? "es" : "en";
      localStorage.setItem("i18nextLng", language);
      i18n.changeLanguage(language);
    }

    const handleEventUser = (data) => {
      if (data.action === "update" && data.user.id === user.id) {
        setUser(data.user);
      }
    }

    socket.on(`company-${companyId}-user`, handleEventUser);

    return () => {
      socket.off(`company-${companyId}-user`, handleEventUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await openApi.post("/auth/login", userData);
      const {
        user: { companyId, id, company, languageId },
      } = data;

      if (has(company, "settings") && isArray(company.settings)) {
        const setting = company.settings.find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      moment.locale('pt-br');
      console.log("data", data)
      console.log("user", data.user)
      const dueDate = data.user.company.dueDate;
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");
      var diff = moment(dueDate).diff(moment(moment()).format());
      console.log("hoje",hoje)
      console.log("vencimento",vencimento)
      var before = moment(moment().format()).isBefore(dueDate);
      console.log("before",before)
      var dias = moment.duration(diff).asDays();
      var diasVenc = vencimento.valueOf() - hoje.valueOf()
      console.log("🚀 Console Log : diasVenc", diasVenc);

      if (before === true) {
        try {
          await Promise.all([
            db.tickets.clear(),
            db.messages.clear(),
            db.contacts.clear(),
            db.queues.clear(),
            db.users.clear(),
            db.syncMeta.clear()
          ]);
        } catch (cacheErr) {
          console.warn("No se pudo limpiar IndexedDB previo:", cacheErr);
        }

        localStorage.setItem("token", JSON.stringify(data.token));
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        localStorage.setItem("companyId", companyId);
        localStorage.setItem("userId", id);
        localStorage.setItem("profile", data.user.profile);
        // localStorage.setItem("userQueues", data.user.queues.map(({ id }) => id)); // Alternativa
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        if (Math.round(dias) < 5) {
          toast.warn(`Tu suscripción vence en ${Math.round(dias)} ${Math.round(dias) === 1 ? 'día' : 'días'}`);
        }
        history.push("/tickets");
        setLoading(false);
      } else {
        toastError(`¡Vaya! Tu suscripción ha vencido,
          Ponte en contacto con el soporte para obtener más información.`);
        setLoading(false);
      }

    } catch (err) {
      setLoading(false);
      console.log(err)
      console.log(err.response)
      if (err.response !== undefined) {
        toastError(err.response.data.error);
      } else if (err) {
        toastError(err);
      } else {
        toastError("¡Vaya! Ocurrió un error inesperado, por favor intente más tarde.");
      }
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      disconnectSocket();

      await new Promise(resolve => setTimeout(resolve, 100));

      localStorage.clear();

      setIsAuth(false);
      setUser({});
      api.defaults.headers.Authorization = undefined;

      history.push("/login");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (err) {
      toastError(err);
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
  };
};

export default useAuth;